import { useEffect } from 'preact/hooks';

/**
 * Track the cursor as normalized -1..1 coordinates and write them to
 * `--m-cursor-x` / `--m-cursor-y` on `<html>`. The aurora background's
 * gradient blobs read these vars to translate slightly toward the
 * cursor — adds depth without making the whole page feel like a
 * parallax demo. Throttled via requestAnimationFrame so a fast mouse
 * doesn't peg the main thread.
 *
 * Reduced-motion users get zeroed values (no movement at all).
 */
export function useCursorParallax(): void {
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (reduced) return;

		let queued = false;
		let lastX = 0;
		let lastY = 0;
		const root = document.documentElement;

		const flush = () => {
			queued = false;
			root.style.setProperty('--m-cursor-x', lastX.toFixed(3));
			root.style.setProperty('--m-cursor-y', lastY.toFixed(3));
		};

		const onMove = (e: PointerEvent) => {
			lastX = (e.clientX / window.innerWidth) * 2 - 1; // -1..1
			lastY = (e.clientY / window.innerHeight) * 2 - 1; // -1..1
			if (!queued) {
				queued = true;
				requestAnimationFrame(flush);
			}
		};

		window.addEventListener('pointermove', onMove, { passive: true });
		return () => window.removeEventListener('pointermove', onMove);
	}, []);
}
